import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Bell, Plus, X } from 'lucide-react'
import { CommunityLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/community/CommunityLayout'
import { useCommunity } from '@/components/community/CommunityContext'
import { AnnouncementCard, SectionLoading } from '@/components/community/ui'
import {
  listAnnouncements, createAnnouncement, toggleAnnouncementPin, deleteAnnouncement,
  type AnnouncementResponse, type AnnouncementRequest,
} from '@/services/communityApi'

const CATEGORIES = ['CAMPAIGN', 'EVENT', 'TRAINING', 'RESOURCE', 'IMPORTANT', 'PLATFORM'] as const

export default function CommunityAnnouncementsPage() {
  const { t } = useTranslation()
  const { community, canManage } = useCommunity()
  const [items, setItems] = useState<AnnouncementResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [showCreate, setShowCreate] = useState(false)

  const load = useCallback(() => {
    if (!community) return
    setLoading(true)
    listAnnouncements(community.id, 0, 50)
      .then((p) => setItems(p.content))
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }, [community])

  useEffect(() => { load() }, [load])

  const onPin = async (a: AnnouncementResponse) => {
    setBusyId(a.id)
    try { const upd = await toggleAnnouncementPin(a.id); setItems((p) => [...p].sort(sortPinned).map((x) => x.id === upd.id ? upd : x)) } catch { /* ignore */ }
    finally { setBusyId(null) }
  }
  const onDelete = async (a: AnnouncementResponse) => {
    setBusyId(a.id)
    try { await deleteAnnouncement(a.id); setItems((p) => p.filter((x) => x.id !== a.id)) } catch { /* ignore */ }
    finally { setBusyId(null) }
  }

  if (!community) return null

  return (
    <CommunityLayout>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <GovPageTitle title={t('community.announcements.title')} subtitle={t('community.announcements.subtitle')} icon={Bell} />
        {canManage && (
          <GovButton variant="primary" size="sm" onClick={() => setShowCreate(true)} className="mt-1">
            <Plus className="w-4 h-4 mr-1" aria-hidden="true" />{t('community.announcements.create')}
          </GovButton>
        )}
      </div>

      {loading ? <SectionLoading rows={3} /> : items.length === 0 ? (
        <GovEmptyState icon="🔔" title={t('community.announcements.none')} />
      ) : (
        <div className="space-y-3">
          {[...items].sort(sortPinned).map((a) => (
            <AnnouncementCard key={a.id} announcement={a} canManage={canManage} onPin={onPin} onDelete={onDelete} busy={busyId === a.id} />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateAnnouncementModal onClose={() => setShowCreate(false)} onCreated={(a) => { setItems((p) => [...p, a].sort(sortPinned)); setShowCreate(false) }} />
      )}
    </CommunityLayout>
  )
}

function sortPinned(a: AnnouncementResponse, b: AnnouncementResponse) {
  if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
  return b.createdAt.localeCompare(a.createdAt)
}

function CreateAnnouncementModal({ onClose, onCreated }: { onClose: () => void; onCreated: (a: AnnouncementResponse) => void }) {
  const { t } = useTranslation()
  const [form, setForm] = useState<AnnouncementRequest>({ title: '', body: '', category: 'IMPORTANT', pinned: false })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true); setError(null)
    try { onCreated(await createAnnouncement(form)) }
    catch (err: any) { setError(err?.response?.data?.message || t('errors.generic')) }
    finally { setSaving(false) }
  }

  const field = (k: keyof AnnouncementRequest, v: string | boolean) => setForm((p) => ({ ...p, [k]: v }))
  const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={t('community.announcements.create')}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <GovCard className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{t('community.announcements.create')}</h2>
          <button onClick={onClose} aria-label={t('community.actions.cancel')} className="p-2 rounded-lg hover:bg-gray-100 min-h-[40px] min-w-[40px] flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.announcements.titleLabel')}</label>
            <input required value={form.title} onChange={(e) => field('title', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.announcements.categoryLabel')}</label>
            <select value={form.category} onChange={(e) => field('category', e.target.value)} className={inputCls}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{t(`community.announcements.category.${c}`, c)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.announcements.bodyLabel')}</label>
            <textarea required value={form.body} onChange={(e) => field('body', e.target.value)} rows={4} className={inputCls} />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 min-h-[40px]">
            <input type="checkbox" checked={!!form.pinned} onChange={(e) => field('pinned', e.target.checked)} className="w-4 h-4" />
            {t('community.announcements.pin')}
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <GovButton variant="secondary" size="sm" onClick={onClose}>{t('community.actions.cancel')}</GovButton>
            <GovButton variant="primary" size="sm" type="submit" disabled={saving}>{saving ? t('common.saving') : t('community.actions.publish')}</GovButton>
          </div>
        </form>
      </GovCard>
    </div>
  )
}